import { useState, useMemo, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { HelpArticle, HelpCategory, HelpSearchResult } from './types';
import { HelpCategoryNav } from './HelpCategoryNav';
import { HelpArticleView } from './HelpArticle';
import { HelpSearch } from './HelpSearch';
import { useTour } from '../tour';
import { allArticles, getCategoryInfo, getArticlesByCategory } from '../../content/help';
import styles from './HelpCenter.module.css';

interface HelpCenterProps {
  isOpen: boolean;
  onClose: () => void;
  isPlatformUser?: boolean;
  initialArticleId?: string;
}

export function HelpCenter({
  isOpen,
  onClose,
  isPlatformUser = false,
  initialArticleId,
}: HelpCenterProps) {
  const [activeCategory, setActiveCategory] = useState<HelpCategory | null>(null);
  const [activeArticle, setActiveArticle] = useState<HelpArticle | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<HelpSearchResult[]>([]);
  const { startTour, getAvailableTours, isTourCompleted } = useTour();

  // Filter articles based on user type
  const availableArticles = useMemo(() => {
    return allArticles.filter(article => {
      const categoryInfo = getCategoryInfo(article.category);
      if (!categoryInfo) return false;
      if (categoryInfo.type === 'common') return true;
      if (isPlatformUser && categoryInfo.type === 'platform') return true;
      if (!isPlatformUser && categoryInfo.type === 'institution') return true;
      return false;
    });
  }, [isPlatformUser]);

  // Handle initial article
  useEffect(() => {
    if (isOpen && initialArticleId) {
      const article = availableArticles.find(a => a.id === initialArticleId);
      if (article) {
        setActiveArticle(article);
        setActiveCategory(article.category);
      }
    }
  }, [isOpen, initialArticleId, availableArticles]);

  // Reset state when closing
  useEffect(() => {
    if (!isOpen) {
      setActiveCategory(null);
      setActiveArticle(null);
      setSearchQuery('');
      setSearchResults([]);
    }
  }, [isOpen]);

  // Search function
  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);

    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    const lowerQuery = query.toLowerCase();
    const words = lowerQuery.split(/\s+/);

    const results: HelpSearchResult[] = availableArticles
      .map(article => {
        let score = 0;
        const matchedKeywords: string[] = [];

        // Check title match (highest weight)
        if (article.title.toLowerCase().includes(lowerQuery)) {
          score += 10;
        }

        // Check keyword matches
        article.keywords.forEach(keyword => {
          if (keyword.toLowerCase().includes(lowerQuery) ||
              lowerQuery.includes(keyword.toLowerCase())) {
            score += 5;
            matchedKeywords.push(keyword);
          }
        });

        // Check summary match
        if (article.summary.toLowerCase().includes(lowerQuery)) {
          score += 3;
        }

        // Check content match
        if (article.content.toLowerCase().includes(lowerQuery)) {
          score += 1;
        }

        // Partial word matching
        words.forEach(word => {
          if (article.title.toLowerCase().includes(word)) score += 2;
          if (article.summary.toLowerCase().includes(word)) score += 1;
        });

        return { article, matchedKeywords, score };
      })
      .filter(result => result.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 10);

    setSearchResults(results);
    setActiveCategory(null);
    setActiveArticle(null);
  }, [availableArticles]);

  // Navigation handlers
  const handleCategorySelect = (category: HelpCategory) => {
    setActiveCategory(category);
    setActiveArticle(null);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleArticleSelect = (article: HelpArticle) => {
    setActiveArticle(article);
  };

  const handleBack = () => {
    if (activeArticle) {
      setActiveArticle(null);
    } else if (activeCategory) {
      setActiveCategory(null);
    }
  };

  const handleStartTour = (tourId: string) => {
    onClose();
    // Small delay to let modal close
    setTimeout(() => {
      startTour(tourId);
    }, 300);
  };

  // Get tours for display
  const tours = getAvailableTours().filter(tour => {
    if (isPlatformUser) return tour.category === 'platform';
    return tour.category === 'institution';
  });

  // Keyboard handling
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (activeArticle) {
          setActiveArticle(null);
        } else if (activeCategory) {
          setActiveCategory(null);
        } else {
          onClose();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, activeArticle, activeCategory, onClose]);

  if (!isOpen) return null;

  const categoryArticles = activeCategory
    ? getArticlesByCategory(activeCategory).filter(a => availableArticles.includes(a))
    : [];

  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            {(activeCategory || activeArticle) && (
              <button className={styles.backButton} onClick={handleBack}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 12H5M12 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            <h2 className={styles.title}>
              {activeArticle
                ? activeArticle.title
                : activeCategory
                  ? getCategoryInfo(activeCategory)?.name
                  : 'Help Center'}
            </h2>
          </div>
          <button className={styles.closeButton} onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Search */}
        {!activeArticle && (
          <div className={styles.searchContainer}>
            <HelpSearch
              value={searchQuery}
              onChange={handleSearch}
              placeholder="Search help articles..."
            />
          </div>
        )}

        {/* Content */}
        <div className={styles.content}>
          {activeArticle ? (
            <HelpArticleView
              article={activeArticle}
              onArticleClick={id => {
                const article = availableArticles.find(a => a.id === id);
                if (article) handleArticleSelect(article);
              }}
            />
          ) : searchQuery ? (
            <div className={styles.searchResults}>
              {searchResults.length === 0 ? (
                <div className={styles.emptySearch}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <circle cx="11" cy="11" r="8" />
                    <path d="M21 21l-4.35-4.35" />
                  </svg>
                  <p>No results found for "{searchQuery}"</p>
                  <span>Try different keywords or browse categories</span>
                </div>
              ) : (
                <>
                  <p className={styles.searchCount}>
                    {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} found
                  </p>
                  <div className={styles.articleList}>
                    {searchResults.map(({ article, matchedKeywords }) => (
                      <button
                        key={article.id}
                        className={styles.articleItem}
                        onClick={() => handleArticleSelect(article)}
                      >
                        <div className={styles.articleItemIcon}>
                          {article.icon || (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                              <polyline points="14 2 14 8 20 8" />
                            </svg>
                          )}
                        </div>
                        <div className={styles.articleItemContent}>
                          <span className={styles.articleItemTitle}>{article.title}</span>
                          <span className={styles.articleItemSummary}>{article.summary}</span>
                          {matchedKeywords.length > 0 && (
                            <div className={styles.matchedKeywords}>
                              {matchedKeywords.slice(0, 3).map(kw => (
                                <span key={kw} className={styles.keyword}>{kw}</span>
                              ))}
                            </div>
                          )}
                        </div>
                        <svg className={styles.articleItemArrow} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M9 18l6-6-6-6" />
                        </svg>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          ) : activeCategory ? (
            <div className={styles.categoryView}>
              <p className={styles.categoryDescription}>
                {getCategoryInfo(activeCategory)?.description}
              </p>
              <div className={styles.articleList}>
                {categoryArticles.map(article => (
                  <button
                    key={article.id}
                    className={styles.articleItem}
                    onClick={() => handleArticleSelect(article)}
                  >
                    <div className={styles.articleItemIcon}>
                      {article.icon || (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                        </svg>
                      )}
                    </div>
                    <div className={styles.articleItemContent}>
                      <span className={styles.articleItemTitle}>{article.title}</span>
                      <span className={styles.articleItemSummary}>{article.summary}</span>
                    </div>
                    <svg className={styles.articleItemArrow} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 18l6-6-6-6" />
                    </svg>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {/* Tours section */}
              {tours.length > 0 && (
                <div className={styles.toursSection}>
                  <h3 className={styles.sectionTitle}>Interactive Tours</h3>
                  <p className={styles.sectionDescription}>
                    Take a guided tour to learn about key features
                  </p>
                  <div className={styles.tourList}>
                    {tours.map(tour => (
                      <button
                        key={tour.id}
                        className={styles.tourItem}
                        onClick={() => handleStartTour(tour.id)}
                      >
                        <div className={styles.tourIcon}>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10" />
                            <polygon points="10 8 16 12 10 16 10 8" fill="currentColor" />
                          </svg>
                        </div>
                        <div className={styles.tourContent}>
                          <span className={styles.tourName}>{tour.name}</span>
                          <span className={styles.tourDescription}>{tour.description}</span>
                        </div>
                        {isTourCompleted(tour.id) && (
                          <span className={styles.tourBadge}>Completed</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Categories */}
              <div className={styles.categoriesSection}>
                <h3 className={styles.sectionTitle}>Browse by Category</h3>
                <HelpCategoryNav
                  isPlatformUser={isPlatformUser}
                  onCategorySelect={handleCategorySelect}
                />
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <span className={styles.footerText}>
            Press <kbd>Esc</kbd> to go back or close
          </span>
        </div>
      </div>
    </div>,
    document.body
  );
}
